import type { ListResponse } from 'Services/SalesManagement/types'
import type { LoadingStates } from 'Store'
import {
  createAxiosAsyncThunkWithParams,
  getAxiosInstance,
  type ValidationErrors
} from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { type AxiosError } from 'axios'

export interface TransferCountShape {
  max: number
  current: number
}

export interface TicketShape {
  id: number
  external_id: string | null
  product_name: string
  ticket_id: string | null
  payment_method: string | null
  external_creation_date: Date
  payment_status: string
  waltti_id: string | null
}

export interface AffiliateProductShape {
  tickets: TicketShape[]
  count: number
  next: string | null
  previous: string | null
  loading: LoadingStates
  singleTicketLoading: LoadingStates
  transferHistoryLoading: LoadingStates
  transferCountLoading: LoadingStates
  searchFieldsLoading: LoadingStates
  singleTicket: TicketShape | null
  singleTicketTransfers: TicketTransferShape[]
  singleTicketTransferCount: TransferCountShape | null
}

const initialState: AffiliateProductShape = {
  tickets: [],
  count: 0,
  next: null,
  previous: null,
  loading: 'idle',
  singleTicketLoading: 'idle',
  transferHistoryLoading: 'idle',
  transferCountLoading: 'idle',
  searchFieldsLoading: 'idle',
  singleTicket: null,
  singleTicketTransfers: [],
  singleTicketTransferCount: null
}

export interface TicketParamShape {
  page: number
  ordering: string
  created_after: string
  created_before: string
  search: string
  search_fields: string
}

type FetchTicketListParams = TicketParamShape

const fetchTicketList = createAxiosAsyncThunkWithParams<
  ListResponse<TicketShape>,
  FetchTicketListParams | undefined
>('affiliateProducts/fetchTicketList', '/api/v1/transfer-management/')

interface SetMaxTransfersRequest {
  id: number
  old_max: number
  new_max: number
}

type SetMaxTransfersResponse = TicketShape

const setMaxTransfers = createAsyncThunk<
  SetMaxTransfersResponse,
  SetMaxTransfersRequest,
  {
    rejectValue: ValidationErrors
  }
>('affiliateProducts/setMaxTransfers', async (data, { rejectWithValue }) => {
  try {
    const { id, ...fields } = data
    const response = await getAxiosInstance().post<SetMaxTransfersResponse>(
      `/api/v1/transfer-management/${id}/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type FetchSingleTicketResponse = TicketShape

interface FetchSingleTicketRequest {
  id: number
}

const fetchSingleTicket = createAsyncThunk<
  FetchSingleTicketResponse,
  FetchSingleTicketRequest,
  {
    rejectValue: ValidationErrors
  }
>('affiliateProducts/fetchSingleTicket', async (data, { rejectWithValue }) => {
  try {
    const { id } = data
    const response = await getAxiosInstance().get<FetchSingleTicketResponse>(
      `/api/v1/transfer-management/${id}/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type FetchTicketTransferCountResponse = TransferCountShape

interface FetchTicketTransferCountRequest {
  id: number
}

const fetchTicketTransferCount = createAsyncThunk<
  FetchTicketTransferCountResponse,
  FetchTicketTransferCountRequest,
  {
    rejectValue: ValidationErrors
  }
>('affiliateProducts/fetchTicketTransferCount', async (data, { rejectWithValue }) => {
  try {
    const { id } = data
    const response = await getAxiosInstance().get<FetchTicketTransferCountResponse>(
      `/api/v1/transfer-management/${id}/transfers/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

export interface TicketTransferShape {
  created: Date
  user: {
    id: number
    username: string
    email: string
  }
  old_max: number
  new_max: number
}

type FetchTicketTransferListResponse = TicketTransferShape[]

interface FetchTicketTransferListRequest {
  id: number
}

const fetchTicketTransferList = createAsyncThunk<
  FetchTicketTransferListResponse,
  FetchTicketTransferListRequest,
  {
    rejectValue: ValidationErrors
  }
>('affiliateProducts/fetchTicketTransferList', async (data, { rejectWithValue }) => {
  try {
    const { id } = data
    const response = await getAxiosInstance().get<FetchTicketTransferListResponse>(
      `/api/v1/transfer-management/${id}/events/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

const genericActions = actionGenerator('affiliateProducts')

const affiliateProductSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchTicketList.fulfilled, (state, { payload }) => {
        state.tickets = payload.results
        state.count = payload.count
        state.next = payload.next
        state.previous = payload.previous
        state.loading = 'fulfilled'
      })
      .addCase(fetchTicketList.pending, state => {
        state.tickets = initialState.tickets
        state.loading = 'pending'
      })
      .addCase(fetchTicketList.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(setMaxTransfers.fulfilled, state => {
        state.transferCountLoading = 'fulfilled'
      })
      .addCase(setMaxTransfers.pending, state => {
        state.transferCountLoading = 'pending'
      })
      .addCase(setMaxTransfers.rejected, state => {
        state.transferCountLoading = 'rejected'
      })

      .addCase(fetchSingleTicket.fulfilled, (state, { payload }) => {
        state.singleTicketLoading = 'fulfilled'
        state.singleTicket = payload
      })
      .addCase(fetchSingleTicket.pending, state => {
        state.singleTicketLoading = 'pending'
      })
      .addCase(fetchSingleTicket.rejected, state => {
        state.singleTicketLoading = 'rejected'
      })

      .addCase(fetchTicketTransferCount.fulfilled, (state, { payload }) => {
        state.transferCountLoading = 'fulfilled'
        state.singleTicketTransferCount = payload
      })
      .addCase(fetchTicketTransferCount.pending, state => {
        state.transferCountLoading = 'pending'
        state.singleTicketTransferCount = initialState.singleTicketTransferCount
      })
      .addCase(fetchTicketTransferCount.rejected, state => {
        state.transferCountLoading = 'rejected'
      })

      .addCase(fetchTicketTransferList.fulfilled, (state, { payload }) => {
        state.transferHistoryLoading = 'fulfilled'
        state.singleTicketTransfers = payload
      })
      .addCase(fetchTicketTransferList.pending, state => {
        state.transferHistoryLoading = 'pending'
        state.singleTicketTransfers = initialState.singleTicketTransfers
      })
      .addCase(fetchTicketTransferList.rejected, state => {
        state.transferHistoryLoading = 'rejected'
      })

      .addCase(genericActions.ResetAction, () => initialState)
  }
})

export default affiliateProductSlice.reducer

export {
  fetchSingleTicket,
  fetchTicketList,
  fetchTicketTransferCount,
  fetchTicketTransferList,
  setMaxTransfers
}
